/**
 * Shared vocabulary for the file pickers that drive a two-file comparison.
 *
 * Both the IFC 3D viewer and the DWG Drawing Compare tool list a project's
 * files, nest superseded revisions under the current one, and pick two of them
 * as the older/newer sides of a diff. The rules for that are identical and
 * file-type agnostic, so they live here rather than in either domain — see
 * `DiffVersionPlaque` for the UI built on top.
 */

export interface FileCandidate {
  docId: number;
  filename: string;
  version: number | null;
  versionGroup: string | null;
  projectId: number | null;
  isLatestVersion: boolean;
  /** Tie-breaker when a version number can't order a pair. */
  createdAt?: string | null;
  size?: number | null;
  /** Resolves the folder-breadcrumb tooltip on a picker row. */
  folderId?: number | null;
}

/** One file in the picker: its current version plus any superseded revisions. */
export interface FileCandidateChain {
  /** Latest version — the row shown at top level. */
  head: FileCandidate;
  /** Superseded revisions, newest first. Empty for single-version files. */
  older: FileCandidate[];
}

/**
 * The version to badge a candidate with, or null when it shouldn't carry one —
 * a "v{n}" badge only earns its place when the file has more than one version
 * (its group has multiple candidates, or it's itself v2+).
 */
export function candidateBadgeVersion(
  cand: FileCandidate,
  versionGroupCounts: Map<string, number>
): number | null {
  if (cand.version == null) return null;
  const multi =
    cand.version > 1 ||
    (cand.versionGroup != null && (versionGroupCounts.get(cand.versionGroup) ?? 0) > 1);
  return multi ? cand.version : null;
}

/**
 * Collapse candidates into one chain per logical file: the latest version at
 * top level, superseded revisions nested under it. Files with no version_group
 * are their own single-entry chain. Chain order follows the (already
 * newest-first) candidate order of each head.
 */
export function groupCandidatesIntoChains(candidates: FileCandidate[]): FileCandidateChain[] {
  const groups = new Map<string, FileCandidate[]>();
  const order: string[] = [];
  for (const c of candidates) {
    // Ungrouped files key on their own id so they can't collide with a chain.
    const key = c.versionGroup ?? `doc:${c.docId}`;
    const bucket = groups.get(key);
    if (bucket) bucket.push(c);
    else {
      groups.set(key, [c]);
      order.push(key);
    }
  }

  return order.map((key) => {
    const members = groups.get(key) as FileCandidate[];
    // Prefer the flagged head, taking the HIGHEST-versioned one when more than
    // one row claims it — callers normalise a null `is_latest_version` to true,
    // so a legacy row could otherwise head a lineage purely by arriving first.
    // Same rule as `sourceHeadIds` in DocumentPicker, so the dropdown's count
    // and the Browse-all modal's rows elect the same head.
    const sorted = [...members].sort((a, b) => (b.version ?? 0) - (a.version ?? 0));
    const head = sorted.find((m) => m.isLatestVersion) ?? sorted[0];
    return { head, older: sorted.filter((m) => m.docId !== head.docId) };
  });
}

/**
 * Order a staged pair older → newer. Version numbers decide it only **within
 * one version chain**, where the upload timestamps can be misleading (a v1
 * re-uploaded after v2 still *is* the older revision). Across two different
 * files "v4" and "v1" say nothing about which is older, so anything that isn't
 * the same `versionGroup` is ordered by `createdAt`; a pair neither rule
 * separates keeps the order it came in.
 */
export function orderCandidatePair<
  T extends Pick<FileCandidate, "version" | "versionGroup" | "createdAt">,
>(a: T, b: T): [older: T, newer: T] {
  const sameChain = a.versionGroup != null && a.versionGroup === b.versionGroup;
  if (sameChain && a.version != null && b.version != null) {
    return a.version <= b.version ? [a, b] : [b, a];
  }
  const at = a.createdAt ? new Date(a.createdAt).getTime() : NaN;
  const bt = b.createdAt ? new Date(b.createdAt).getTime() : NaN;
  if (Number.isFinite(at) && Number.isFinite(bt)) return at <= bt ? [a, b] : [b, a];
  return [a, b];
}
